import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {ClientService} from 'services';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {nextStep} from 'commons/utils/Functions';
import ConsigneeAddressForm from './form/AddressForm';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {useBeneficiaryContext} from 'context/beneficiary-context/BeneficiaryContext';
import {AddressServiceRequest, EfeAccordionValues, EfeResponse, Field} from 'commons/models';

function ConsigneeAddress() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const {menu, expedientId, kycId} = useMenuContext();
    const {tempBeneficiary, setTempBeneficiary, setActive} = useBeneficiaryContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [consigneeData, setConsigneeData] = React.useState<AddressServiceRequest>();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    async function onSubmit(values: AddressServiceRequest) {
        setIsLoading(true);
        const request: AddressServiceRequest = {
            ...values,
            kycId: kycId,
            viewId: flow.data.viewId,
            viewFatherId: flow.data.fatherId,
            expedientId: expedientId,
        };
        try {
            const response: EfeResponse<AddressServiceRequest> =
                await ClientService.saveConsigneeAddress(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                if (!consigneeData) {
                    ClientService.notifyConsignee(kycId);
                }
                const path = nextStep(flow, menu);
                if (tempBeneficiary) {
                    setActive(false);
                    setTempBeneficiary(undefined);
                }
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            showAlertError(error, Messages.ERROR_SAVING_ADDRESS);
        }
    }

    React.useEffect(() => {
        if (kycId > 0) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<AddressServiceRequest> =
                        await ClientService.getConsigneeAddress(kycId);
                    if (response && response.data) {
                        setConsigneeData(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setConsigneeData(undefined);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Direcci\u00F3n',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <ConsigneeAddressForm
                            kycId={kycId}
                            viewId={flow.data.viewId}
                            submitHandler={onSubmit}
                            viewKey={flow.data.id}
                            consigneeData={consigneeData}
                            fields={viewFields}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.V_CONSIGNEE_ADDRESS === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.CONSIGNEE_ADDRESS)}
                values={accordion}
            />
        </MainContent>
    );
}

export default ConsigneeAddress;
